import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';
import { useSubathonOverlay } from '../subathon/useSubathonOverlay';
import { SubathonOverlayName } from '../subathon/types';
import {
  SubathonOverlayGlobalStyle,
  SubathonSummaryBlock,
  SubathonTimerBlock,
} from '../subathon/SubathonOverlayViews';
import { useOverlayFonts } from '../hooks/useOverlayFonts';

const VALID_OVERLAYS: SubathonOverlayName[] = ['timer', 'summary'];

const SubathonOverlayPage: React.FC = () => {
  useSeo({ title: 'Extensible overlay', noIndex: true });
  useOverlayFonts();

  const params = useParams<{ overlayKey: string; overlay: string }>();
  const [searchParams] = useSearchParams();

  const overlay = (VALID_OVERLAYS.includes(params.overlay as SubathonOverlayName)
    ? params.overlay
    : 'timer') as SubathonOverlayName;
  const demo = searchParams.get('demo') === '1';

  const { style, elements, state, remainingMs, flashes, error } = useSubathonOverlay(
    params.overlayKey,
    overlay,
    demo,
  );

  if (error) {
    return (
      <>
        <SubathonOverlayGlobalStyle />
        <div style={{ color: '#ff6b6b', fontFamily: 'sans-serif', padding: '1rem', fontSize: 14 }}>
          {error}
        </div>
      </>
    );
  }

  if (!style || !state) {
    return <SubathonOverlayGlobalStyle />;
  }

  const justify =
    style.align === 'right' ? 'flex-end' : style.align === 'center' ? 'center' : 'flex-start';
  const scale = Math.max(0.3, Math.min(3, (style.scale || 100) / 100));
  const width = Math.max(0, parseInt(elements.width, 10) || 0);

  const body =
    overlay === 'summary' ? (
      <SubathonSummaryBlock
        style={style}
        elements={elements}
        state={state}
        remainingMs={remainingMs}
      />
    ) : (
      <SubathonTimerBlock
        style={style}
        elements={elements}
        timer={state.timer}
        remainingMs={remainingMs}
        totalAddedMs={state.timer.addedMs}
        flashes={flashes}
      />
    );

  return (
    <>
      <SubathonOverlayGlobalStyle />
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: justify,
          padding: 8,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: `top ${style.align === 'right' ? 'right' : style.align === 'center' ? 'center' : 'left'}`,
            width: width > 0 ? width : 'auto',
            maxWidth: '100%',
            minWidth: width > 0 ? undefined : 300,
          }}
        >
          {body}
        </div>
      </div>
    </>
  );
};

export default SubathonOverlayPage;
